import React, { useState, useEffect, memo } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Table,
  Row,
  Col,
  Card,
  CardBody,
  Label,
} from "reactstrap";
import Select from "react-select";
import PerfectScrollbar from "react-perfect-scrollbar";
import { AddCommaNumber } from "../../../helpers/Utils";
import { LogsValueOvertimeCoefficient } from "../../../helpers/UserStaff";

const LOG_COLUMNS = [
  { key: "commission", label: "Hoa hồng", type: "money", colSize: 2 },
  { key: "bonus", label: "Thưởng", type: "money", colSize: 2 },
  { key: "overtime_hour", label: "Tăng ca (h)", type: "hour", colSize: 2 },
  {
    key: "overtime_coefficient",
    label: "Hệ số tăng ca",
    type: "select",
    colSize: 1,
  },
  { key: "advence", label: "Tạm ứng", type: "money", colSize: 2 },
  { key: "fined", label: "Phạt", type: "money", colSize: 1 },
  { key: "day_off_hour", label: "Nghỉ (h)", type: "hour", colSize: 2 },
];

const ContentComponent = ({ title, isOpen, toggle, onSubmit, data_all }) => {
  // State: staffLogs = { [staffId]: { commission: '', bonus: '', ... } }
  const [staffLogs, setStaffLogs] = useState({});
  // State cho form nhập chung
  const [commonValues, setCommonValues] = useState({
    commission: "",
    bonus: "",
    overtime_hour: "",
    overtime_coefficient: "",
    advence: "",
    fined: "",
    day_off_hour: "",
  });

  // Filter only active staff (not resigned)
  const activeStaff = data_all
    ? data_all.filter((staff) => staff.status_working !== "off")
    : [];

  // Initialize staffLogs when data_all changes
  useEffect(() => {
    if (activeStaff && activeStaff.length > 0) {
      const initialLogs = {};
      activeStaff.forEach((staff) => {
        initialLogs[staff.id] = {
          commission: "",
          bonus: "",
          overtime_hour: "",
          overtime_coefficient: "",
          advence: "",
          fined: "",
          day_off_hour: "",
        };
      });
      setStaffLogs(initialLogs);
    }
  }, [data_all]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && activeStaff && activeStaff.length > 0) {
      const initialLogs = {};
      activeStaff.forEach((staff) => {
        initialLogs[staff.id] = {
          commission: "",
          bonus: "",
          overtime_hour: "",
          overtime_coefficient: "",
          advence: "",
          fined: "",
          day_off_hour: "",
        };
      });
      setStaffLogs(initialLogs);
      setCommonValues({
        commission: "",
        bonus: "",
        overtime_hour: "",
        overtime_coefficient: "",
        advence: "",
        fined: "",
        day_off_hour: "",
      });
    }
  }, [isOpen, data_all]);

  // Handle input change for a specific staff and log type
  const handleInputChange = (staffId, logType, value) => {
    // Remove dots for money values only, keep decimal for select fields
    const column = LOG_COLUMNS.find((c) => c.key === logType);
    const cleanValue =
      column?.type === "select" ? value : value.replace(/\./g, "");

    setStaffLogs((prev) => ({
      ...prev,
      [staffId]: {
        ...prev[staffId],
        [logType]: cleanValue,
      },
    }));
  };

  // Handle common value change
  const handleCommonChange = (logType, value) => {
    // Remove dots for money values only, keep decimal for select fields
    const column = LOG_COLUMNS.find((c) => c.key === logType);
    const cleanValue =
      column?.type === "select" ? value : value.replace(/\./g, "");
    setCommonValues((prev) => ({
      ...prev,
      [logType]: cleanValue,
    }));
  };

  // Apply common values to all staff
  const applyCommonValues = () => {
    if (!activeStaff || activeStaff.length === 0) return;

    const newLogs = { ...staffLogs };
    activeStaff.forEach((staff) => {
      Object.keys(commonValues).forEach((key) => {
        if (commonValues[key]) {
          newLogs[staff.id] = {
            ...newLogs[staff.id],
            [key]: commonValues[key],
          };
        }
      });
    });
    setStaffLogs(newLogs);
  };

  // Submit handler - only submit fields with values
  const handleSubmit = (e) => {
    e.preventDefault();

    // Build logs array for submission - only fields with value > 0
    const logsToSubmit = [];

    Object.keys(staffLogs).forEach((staffId) => {
      const staffLog = staffLogs[staffId];
      Object.keys(staffLog).forEach((logType) => {
        const value = staffLog[logType];
        if (value && parseFloat(value) > 0) {
          logsToSubmit.push({
            staffId,
            logType,
            value: parseFloat(value),
          });
        }
      });
    });

    if (logsToSubmit.length === 0) {
      return;
    }

    onSubmit(logsToSubmit);
  };

  // Format display value based on type
  const formatValue = (value, type) => {
    if (!value) return "";
    if (type === "money") return AddCommaNumber(value);
    return value;
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      backdrop={true}
      className="modal-staff-log"
      style={{ maxWidth: "95vw", width: "95vw" }}
    >
      <ModalHeader toggle={toggle}>
        {title || "Thêm thông tin lương nhân viên"}
      </ModalHeader>
      <ModalBody>
        {/* Form nhập giá trị chung cho tất cả nhân viên */}
        <Card className="mb-3">
          <CardBody className="p-3">
            <Row className="justify-content-between align-items-center p-3">
              <strong>
                <i className="simple-icon-people mr-2" />
                Áp dụng cho tất cả:
              </strong>
              <Button color="info" size="sm" onClick={applyCommonValues}>
                <i className="simple-icon-arrow-down mr-1" />
                Áp dụng
              </Button>
            </Row>
            <Row className="align-items-center">
              {LOG_COLUMNS.map((col) => (
                <Col
                  key={col.key}
                  xxs="6"
                  md={col.colSize}
                  className="mb-2 mb-md-0"
                >
                  <div className="d-flex align-items-center has-float-label">
                    <Label className="mr-1 text-nowrap">{col.label}</Label>
                    {col.type === "select" ? (
                      <Select
                        className="react-select w-100"
                        classNamePrefix="react-select"
                        options={LogsValueOvertimeCoefficient()}
                        value={
                          commonValues[col.key]
                            ? {
                                label: commonValues[col.key],
                                value: commonValues[col.key],
                              }
                            : null
                        }
                        onChange={(e) =>
                          handleCommonChange(col.key, e ? e.value : "")
                        }
                        placeholder="Chọn..."
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          control: (base) => ({ ...base, minHeight: "31px" }),
                        }}
                      />
                    ) : (
                      <Input
                        type="text"
                        bsSize="sm"
                        placeholder="0"
                        value={formatValue(commonValues[col.key], col.type)}
                        onChange={(e) =>
                          handleCommonChange(col.key, e.target.value)
                        }
                        style={{ textAlign: "right" }}
                      />
                    )}
                  </div>
                </Col>
              ))}
            </Row>
          </CardBody>
        </Card>

        {/* Bảng nhập riêng từng nhân viên */}
        <PerfectScrollbar
          options={{
            suppressScrollX: false,
            wheelPropagation: false,
          }}
          style={{ maxHeight: "50vh" }}
        >
          <Table responsive hover size="sm" className="table-staff-log">
            <thead className="sticky-top">
              <tr>
                <th style={{ minWidth: "150px" }}>Nhân viên</th>
                {LOG_COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    style={{ minWidth: "100px", textAlign: "center" }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {activeStaff &&
                activeStaff.map((staff) => (
                  <tr key={staff.id}>
                    <td>
                      <strong>{staff.name || staff.label}</strong>
                      {staff.permission_name && (
                        <small className="d-block text-muted">
                          {staff.permission_name}
                        </small>
                      )}
                    </td>
                    {LOG_COLUMNS.map((col) => (
                      <td key={col.key}>
                        {col.type === "select" ? (
                          <Select
                            className="react-select"
                            classNamePrefix="react-select"
                            options={LogsValueOvertimeCoefficient()}
                            value={
                              staffLogs[staff.id]?.[col.key]
                                ? {
                                    label: staffLogs[staff.id]?.[col.key],
                                    value: staffLogs[staff.id]?.[col.key],
                                  }
                                : null
                            }
                            onChange={(e) =>
                              handleInputChange(
                                staff.id,
                                col.key,
                                e ? e.value : "",
                              )
                            }
                            placeholder="Chọn..."
                            isClearable
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              control: (base) => ({
                                ...base,
                                minHeight: "31px",
                                minWidth: "80px",
                              }),
                            }}
                          />
                        ) : (
                          <Input
                            type="text"
                            bsSize="sm"
                            placeholder="0"
                            value={formatValue(
                              staffLogs[staff.id]?.[col.key],
                              col.type,
                            )}
                            onChange={(e) =>
                              handleInputChange(
                                staff.id,
                                col.key,
                                e.target.value,
                              )
                            }
                            style={{ textAlign: "right" }}
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </Table>
        </PerfectScrollbar>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Hủy
        </Button>{" "}
        <Button color="primary" onClick={handleSubmit}>
          <i className="simple-icon-check mr-1" />
          Lưu tất cả
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default memo(ContentComponent);
